/**
 * Özellik isteği metninin kuralları — saf fonksiyonlar, Electron'dan bağımsız
 * ki `npm run test:prefs` ile doğrulanabilsin (bkz. featureRequests.ts).
 *
 * Aynı sınırlar veritabanında da var (0018_feature_requests.sql): uygulama
 * atlatılsa bile uzunluk, kontrol karakterleri ve sıklık denetleniyor.
 */

export const FEATURE_REQUEST_MIN_LENGTH = 10;
export const FEATURE_REQUEST_MAX_LENGTH = 2000;
/** Bağlantı sayısı sınırı — reklam/spam metinleri geçmesin. */
export const FEATURE_REQUEST_MAX_LINKS = 3;
/** Aynı oturumda iki gönderim arası en az bu kadar. Asıl sınır veritabanında. */
export const FEATURE_REQUEST_COOLDOWN_MS = 30_000;

export type FeatureRequestError = 'empty' | 'too_short' | 'too_long' | 'markup' | 'too_many_links' | 'no_text';

/** Sıfır genişlikli ve yazı yönünü değiştiren karakterler (metni olduğundan farklı gösterebilir). */
const INVISIBLE = /[​-‏‪-‮⁠-⁩﻿]/g;
/** Satır sonu ve sekme dışındaki kontrol karakterleri. */
// eslint-disable-next-line no-control-regex
const CONTROL = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g;

/**
 * Betik ya da HTML enjeksiyonu kalıpları. Metin hiçbir yerde HTML olarak
 * çizilmiyor, yine de bu kalıplar hiç kabul edilmiyor (derinlemesine savunma):
 * isteği okuyacak bir yönetim ekranı ileride yanlışlıkla HTML çizerse bile
 * içeride çalışacak bir şey olmasın.
 */
const MARKUP_PATTERNS = [
  /<\s*\/?\s*(script|iframe|frame|object|embed|applet|svg|math|img|video|audio|style|link|meta|base|form|input|button|textarea)\b/i,
  /\b(javascript|vbscript|livescript)\s*:/i,
  /\bdata\s*:\s*(text\/html|application\/(x-)?javascript|image\/svg)/i,
  /\bon(error|load|click|mouse\w*|focus|blur|submit|change|input|key\w*|pointer\w*|animation\w*|toggle)\s*=/i,
  /<\?php\b/i,
];

/** Görünmez karakterleri atar, satır sonlarını ve boşlukları sadeleştirir. */
export function normalizeFeatureRequest(value: string): string {
  return value
    .normalize('NFC')
    .replace(/\r\n?/g, '\n')
    .replace(INVISIBLE, '')
    .replace(/\t/g, ' ')
    .replace(CONTROL, '')
    .split('\n')
    .map((line) => line.replace(/[  ]+$/g, ''))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** Karakter sayısı veritabanındaki `char_length` gibi (kod noktası) sayılır. */
export function featureRequestLength(value: string): number {
  return [...value].length;
}

export function checkFeatureRequest(
  value: unknown
): { ok: true; text: string } | { ok: false; error: FeatureRequestError } {
  if (typeof value !== 'string') return { ok: false, error: 'empty' };
  // Çok büyük girdi işlenmeden reddedilsin.
  if (value.length > FEATURE_REQUEST_MAX_LENGTH * 4) return { ok: false, error: 'too_long' };

  const text = normalizeFeatureRequest(value);
  const length = featureRequestLength(text);
  if (length === 0) return { ok: false, error: 'empty' };
  if (length < FEATURE_REQUEST_MIN_LENGTH) return { ok: false, error: 'too_short' };
  if (length > FEATURE_REQUEST_MAX_LENGTH) return { ok: false, error: 'too_long' };
  if (MARKUP_PATTERNS.some((pattern) => pattern.test(text))) return { ok: false, error: 'markup' };

  const links = text.match(/\bhttps?:\/\/|\bwww\./gi) ?? [];
  if (links.length > FEATURE_REQUEST_MAX_LINKS) return { ok: false, error: 'too_many_links' };

  // En az birkaç harf: yalnızca sembol/sayı yığını bir istek değil.
  if ((text.match(/\p{L}/gu) ?? []).length < 5) return { ok: false, error: 'no_text' };

  return { ok: true, text };
}
